import { PDFDocument, StandardFonts, rgb, type PDFImage } from "pdf-lib";
import { toWinAnsi, wrapText } from "@/domains/documents/utils/templateFormPdfRenderer";
import {
  ACCENT,
  CONTENT_WIDTH,
  MARGIN,
  MUTED,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  RULE,
  SURFACE,
  capLines,
  drawRule,
  drawText,
  drawWrapped,
  ellipsise,
  drawSectionHeading,
  ensureSpace,
  linesPerPage,
  type PdfSheet,
} from "@/domains/pm/utils/protocolPdfSheet";
import { PROTOCOL_COLUMNS, type ProtocolColumnId } from "@/domains/pm/utils/protocol";
import { drawColumnIcon } from "@/domains/pm/utils/protocolPdfIcons";
import { createProtocolLinks, finishProtocolLinks } from "@/domains/pm/utils/protocolPdfLinks";
import { drawActivity, drawComments } from "@/domains/pm/utils/protocolPdfDiscussion";
import { drawNotes } from "@/domains/pm/utils/protocolPdfNotes";
import {
  countNotIncluded,
  drawAttachments,
  drawThumbCell,
  measureThumbCell,
  resolveAttachments,
  type ResolvedAttachments,
} from "@/domains/pm/utils/protocolPdfAttachments";
import { SUMMARY_STRIPES, protocolPdfTheme } from "@/domains/pm/utils/protocolPdfTheme";
import { pacer, throwIfAborted, type Pacer } from "@/domains/pm/utils/protocolPdfPacer";
import type { ProtocolDocumentIssue, ProtocolDocumentModel } from "@/domains/pm/utils/protocol";
import type { ProtocolOriginal } from "@/domains/pm/utils/protocolAttachments";
import { fill } from "@/lib/i18n/fill";
/** The columns whose cells are the issue's own text. */
type TextColumnKey = keyof Pick<
  ProtocolDocumentIssue,
  | "number"
  | "title"
  | "status"
  | "assignees"
  | "disciplines"
  | "startDate"
  | "dueDate"
  | "commentCount"
>;

interface TableColumn {
  /** `attachments` is the thumbnail cell; every other key is text. */
  key: TextColumnKey | "attachments";
  label: string;
  width: number;
}

async function embedBrandLogo(doc: PDFDocument): Promise<PDFImage | null> {
  try {
    const response = await fetch("/franklin-logomark.png");
    if (!response.ok) return null;
    return await doc.embedPng(await response.arrayBuffer());
  } catch {
    return null;
  }
}

const WHITE = rgb(1, 1, 1);
/** The band layout's date and caveat text: white would shout, grey vanishes. */
const ON_BAND_MUTED = rgb(0.78, 0.81, 0.86);

/** The page-top furniture, in the layout's own treatment. */
function drawHeader(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  switch (sheet.theme.header) {
    case "letterhead":
      drawBrandHeader(sheet, model);
      drawTitle(sheet, model);
      return;
    case "band":
      drawBandHeader(sheet, model);
      return;
    case "inline":
      drawInlineHeader(sheet, model);
      return;
  }
}

function drawBrandHeader(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const top = sheet.y;
  if (sheet.logo) {
    const scaled = sheet.logo.scaleToFit(30, 30);
    sheet.page.drawImage(sheet.logo, {
      x: MARGIN,
      y: top - scaled.height,
      width: scaled.width,
      height: scaled.height,
    });
  } else {
    drawText(sheet, "FRANKLIN", MARGIN, top - 18, { font: sheet.bold, size: 12, color: ACCENT });
  }
  // The author's date, not the word "Protocol": the title already prints
  // large directly below, so repeating it here said nothing.
  if (model.date) {
    const dateWidth = sheet.regular.widthOfTextAtSize(toWinAnsi(model.date), 9);
    drawText(sheet, model.date, PAGE_WIDTH - MARGIN - dateWidth, top - 18, {
      size: 9,
      color: MUTED,
    });
  }
  sheet.y = top - 40;
  drawRule(sheet, sheet.y);
  sheet.y -= 28;
}

function drawTitle(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const height = drawWrapped(sheet, model.title, MARGIN, sheet.y, CONTENT_WIDTH, {
    font: sheet.bold,
    size: sheet.theme.titleSize,
    lineHeight: sheet.theme.titleLineHeight,
  });
  sheet.y -= height + 12;
}

const BAND_HEIGHT = 104;

/**
 * Modern: a full-width coloured band carrying the logo, the date and the title,
 * with a thin brand stripe along its foot. The logomark is drawn straight on
 * the band: its red reads against the dark fill, and a white tile behind it
 * looked like a missing image.
 */
function drawBandHeader(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const { theme } = sheet;
  const bottom = PAGE_HEIGHT - BAND_HEIGHT;
  sheet.page.drawRectangle({
    x: 0,
    y: bottom,
    width: PAGE_WIDTH,
    height: BAND_HEIGHT,
    color: theme.bandColor,
  });
  sheet.page.drawRectangle({
    x: 0,
    y: bottom - 3,
    width: PAGE_WIDTH,
    height: 3,
    color: theme.accent,
  });
  const tileTop = PAGE_HEIGHT - 24;
  if (sheet.logo) {
    const scaled = sheet.logo.scaleToFit(24, 24);
    sheet.page.drawImage(sheet.logo, {
      x: MARGIN,
      y: tileTop - 13 - scaled.height / 2,
      width: scaled.width,
      height: scaled.height,
    });
  } else {
    drawText(sheet, "FRANKLIN", MARGIN, tileTop - 17, { font: sheet.bold, size: 12, color: WHITE });
  }
  if (model.date) {
    const dateWidth = sheet.regular.widthOfTextAtSize(toWinAnsi(model.date), 9);
    drawText(sheet, model.date, PAGE_WIDTH - MARGIN - dateWidth, tileTop - 16, {
      size: 9,
      color: ON_BAND_MUTED,
    });
  }
  drawText(
    sheet,
    ellipsise(model.title, sheet.bold, theme.titleSize, CONTENT_WIDTH),
    MARGIN,
    bottom + 20,
    { font: sheet.bold, size: theme.titleSize, color: WHITE }
  );
  sheet.y = bottom - 3 - 26;
}

/** Compact: the logo and the title share one line over a hairline. */
function drawInlineHeader(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const { theme } = sheet;
  const top = sheet.y;
  let titleX = MARGIN;
  if (sheet.logo) {
    const scaled = sheet.logo.scaleToFit(18, 18);
    sheet.page.drawImage(sheet.logo, {
      x: MARGIN,
      y: top - 18,
      width: scaled.width,
      height: scaled.height,
    });
    titleX = MARGIN + scaled.width + 8;
  }
  let dateWidth = 0;
  if (model.date) {
    dateWidth = sheet.regular.widthOfTextAtSize(toWinAnsi(model.date), 8.5);
    drawText(sheet, model.date, PAGE_WIDTH - MARGIN - dateWidth, top - 14, {
      size: 8.5,
      color: MUTED,
    });
  }
  const titleWidth = PAGE_WIDTH - MARGIN - titleX - (dateWidth > 0 ? dateWidth + 12 : 0);
  drawText(
    sheet,
    ellipsise(model.title, sheet.bold, theme.titleSize, titleWidth),
    titleX,
    top - 15,
    {
      font: sheet.bold,
      size: theme.titleSize,
    }
  );
  sheet.y = top - 26;
  drawRule(sheet, sheet.y);
  sheet.y -= 14;
}

function metadataRows(model: ProtocolDocumentModel): Array<[string, string]> {
  const rows: Array<[string, string]> = [
    [model.labels.period, model.period],
    [model.labels.building, model.building],
    [model.labels.project, model.project],
    [model.labels.contents, model.contents],
  ];
  // Beside the period it would otherwise appear to cover in full, and above
  // the timestamp, so a partial protocol declares itself in the same panel a
  // reader already looks at to place the document.
  if (model.selection) rows.push([model.labels.selection, model.selection]);
  rows.push([model.labels.generated, model.generated]);
  return rows;
}

function drawMetadata(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  switch (sheet.theme.metadata) {
    case "panel":
      drawMetadataPanel(sheet, model);
      break;
    case "grid":
      drawMetadataGrid(sheet, model);
      break;
    case "inline":
      drawMetadataInline(sheet, model);
      break;
  }
  drawTruncationNotice(sheet, model);
  sheet.y -= 12;
}

function drawMetadataPanel(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const labelWidth = 86;
  const rowHeight = 20;
  const rows = metadataRows(model);
  const height = rows.length * rowHeight + 12;
  ensureSpace(sheet, height);
  sheet.page.drawRectangle({
    x: MARGIN,
    y: sheet.y - height,
    width: CONTENT_WIDTH,
    height,
    color: SURFACE,
  });
  // The value column keeps the same 10pt padding on the right as the label
  // does on the left, so a long name is clipped inside the panel.
  const valueWidth = CONTENT_WIDTH - labelWidth - 20;
  rows.forEach(([label, value], index) => {
    const baseline = sheet.y - 17 - index * rowHeight;
    drawText(sheet, label, MARGIN + 10, baseline, { font: sheet.bold, size: 9, color: MUTED });
    drawText(
      sheet,
      ellipsise(value, sheet.regular, 9.5, valueWidth),
      MARGIN + 10 + labelWidth,
      baseline,
      {
        size: 9.5,
      }
    );
  });
  sheet.y -= height + 12;
}

/** Modern: two columns of a small capitalised label over its value, between
 *  two hairlines rather than on a shaded panel. */
function drawMetadataGrid(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const rows = metadataRows(model);
  const cellHeight = 31;
  const columnWidth = CONTENT_WIDTH / 2;
  const height = Math.ceil(rows.length / 2) * cellHeight + 8;
  ensureSpace(sheet, height);
  drawRule(sheet, sheet.y);
  rows.forEach(([label, value], index) => {
    const x = MARGIN + (index % 2) * columnWidth;
    const top = sheet.y - 8 - Math.floor(index / 2) * cellHeight;
    drawText(sheet, ellipsise(label.toUpperCase(), sheet.bold, 7, columnWidth - 14), x, top - 7, {
      font: sheet.bold,
      size: 7,
      color: MUTED,
    });
    drawText(sheet, ellipsise(value, sheet.regular, 10, columnWidth - 14), x, top - 20, {
      size: 10,
    });
  });
  sheet.y -= height;
  drawRule(sheet, sheet.y);
  sheet.y -= 14;
}

/** Compact: a dense two-column label/value list, no panel. */
function drawMetadataInline(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const rows = metadataRows(model);
  const rowHeight = 12.5;
  const columnWidth = CONTENT_WIDTH / 2;
  const labelWidth = 62;
  const height = Math.ceil(rows.length / 2) * rowHeight;
  ensureSpace(sheet, height);
  rows.forEach(([label, value], index) => {
    const x = MARGIN + (index % 2) * columnWidth;
    const baseline = sheet.y - 9 - Math.floor(index / 2) * rowHeight;
    drawText(sheet, ellipsise(label, sheet.bold, 7.5, labelWidth - 6), x, baseline, {
      font: sheet.bold,
      size: 7.5,
      color: MUTED,
    });
    drawText(
      sheet,
      ellipsise(value, sheet.regular, 8.5, columnWidth - labelWidth - 10),
      x + labelWidth,
      baseline,
      { size: 8.5 }
    );
  });
  sheet.y -= height + 10;
}

/**
 * An export that is not the whole period says so, right under the metadata:
 * either it hit the activity cap, or changes were lost because their issue
 * could not be read. Both leave the reader with a document that would
 * otherwise pass as a complete record.
 */
function drawTruncationNotice(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const notices = [
    model.truncated ? model.labels.truncated : null,
    model.omitted ? model.labels.omitted : null,
    model.commentsTruncated ? model.labels.commentsTruncated : null,
  ].filter((notice): notice is string => notice !== null);
  for (const notice of notices) {
    ensureSpace(sheet, 30);
    sheet.y -=
      drawWrapped(sheet, notice, MARGIN, sheet.y, CONTENT_WIDTH, {
        size: 9,
        color: sheet.theme.accent,
      }) + 4;
  }
}

type SummaryBucket = keyof ProtocolDocumentModel["summary"];

function summaryEntries(model: ProtocolDocumentModel): Array<[string, number, SummaryBucket]> {
  return [
    [model.labels.inProgress, model.summary.inProgress, "inProgress"],
    [model.labels.resolved, model.summary.resolved, "resolved"],
    [model.labels.closed, model.summary.closed, "closed"],
  ];
}

function drawSummary(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  if (!model.include.summary) return;
  switch (sheet.theme.summary) {
    case "tiles":
      drawSummaryTiles(sheet, model);
      return;
    case "cards":
      drawSummaryCards(sheet, model);
      return;
    case "line":
      drawSummaryLine(sheet, model);
      return;
  }
}

/** One size under the layout's section headings, as the summary always was. */
function drawSummaryHeading(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  drawSectionHeading(sheet, model.labels.summary, sheet.y - 11, sheet.theme.sectionHeadingSize - 1);
}

function drawSummaryTiles(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  ensureSpace(sheet, 58);
  drawSummaryHeading(sheet, model);
  sheet.y -= 24;
  const entries = summaryEntries(model);
  const width = CONTENT_WIDTH / entries.length;
  entries.forEach(([label, count], index) => {
    const x = MARGIN + index * width;
    sheet.page.drawRectangle({
      x,
      y: sheet.y - 29,
      width: width - 6,
      height: 29,
      color: SURFACE,
    });
    const value = String(count);
    drawText(sheet, value, x + 8, sheet.y - 19, { font: sheet.bold, size: 12 });
    // The label follows the measured count, not a fixed offset: a four-digit
    // bucket is wider than the 21pt a hardcoded x + 29 leaves it, and would
    // otherwise be drawn straight over its own label.
    const labelX = x + 8 + sheet.bold.widthOfTextAtSize(toWinAnsi(value), 12) + 5;
    const labelWidth = x + width - 10 - labelX;
    drawText(sheet, ellipsise(label, sheet.regular, 8.5, labelWidth), labelX, sheet.y - 18, {
      size: 8.5,
      color: MUTED,
    });
  });
  sheet.y -= 48;
}

/** Modern: outlined cards, each with its bucket's colour down the left edge,
 *  the count large over its label. */
function drawSummaryCards(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const cardHeight = 42;
  ensureSpace(sheet, 26 + cardHeight + 22);
  drawSummaryHeading(sheet, model);
  sheet.y -= 26;
  const entries = summaryEntries(model);
  const width = CONTENT_WIDTH / entries.length;
  entries.forEach(([label, count, bucket], index) => {
    const x = MARGIN + index * width;
    const cardWidth = width - 8;
    sheet.page.drawRectangle({
      x,
      y: sheet.y - cardHeight,
      width: cardWidth,
      height: cardHeight,
      borderColor: RULE,
      borderWidth: 0.6,
    });
    sheet.page.drawRectangle({
      x,
      y: sheet.y - cardHeight,
      width: 3,
      height: cardHeight,
      color: SUMMARY_STRIPES[bucket],
    });
    drawText(sheet, String(count), x + 12, sheet.y - 20, { font: sheet.bold, size: 15 });
    drawText(sheet, ellipsise(label, sheet.regular, 8, cardWidth - 18), x + 12, sheet.y - 33, {
      size: 8,
      color: MUTED,
    });
  });
  sheet.y -= cardHeight + 22;
}

/** Compact: the three counts on one line, under no heading of their own. */
function drawSummaryLine(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const lineHeight = 13;
  ensureSpace(sheet, lineHeight + 8);
  const baseline = sheet.y - 9;
  let x = MARGIN;
  const label = `${model.labels.summary}:`;
  drawText(sheet, label, x, baseline, { font: sheet.bold, size: 8.5 });
  x += sheet.bold.widthOfTextAtSize(toWinAnsi(label), 8.5) + 8;
  const separator = " · ";
  summaryEntries(model).forEach(([name, count], index) => {
    const value = String(count);
    if (index > 0) {
      drawText(sheet, separator, x, baseline, { size: 8.5, color: MUTED });
      x += sheet.regular.widthOfTextAtSize(toWinAnsi(separator), 8.5);
    }
    drawText(sheet, value, x, baseline, { font: sheet.bold, size: 8.5 });
    x += sheet.bold.widthOfTextAtSize(toWinAnsi(value), 8.5) + 3;
    drawText(sheet, name, x, baseline, { size: 8.5, color: MUTED });
    x += sheet.regular.widthOfTextAtSize(toWinAnsi(name), 8.5);
  });
  sheet.y -= lineHeight + 8;
}

/**
 * Fixed widths for the narrow columns; the title absorbs the rest.
 *
 * Derived rather than declared, because the header band and the row rectangle
 * are both painted at CONTENT_WIDTH while the cells are laid out by a running
 * `x += width` cursor. Hand-tuned widths summed to it by luck and only for the
 * full set — drop one column and the text stopped short inside a full-width
 * band. Letting the title take the remainder makes the sum exact for every
 * subset, which the width test asserts for every combination.
 */
const NARROW_COLUMN_WIDTHS: Record<Exclude<TextColumnKey, "title">, number> = {
  number: 38,
  status: 62,
  assignees: 92,
  disciplines: 58,
  // 66 is the CEILING, not a preference: with every optional column ticked the
  // narrow widths plus `number` must still leave the title its 60pt floor, and
  // 38 + 413 + 60 is 511 of the 511.28 available. Widening this (or adding a
  // seventh column) makes the title clamp and the row overrun the band it is
  // painted on — the width test walks all 2^n subsets and will say so.
  // Both headers fit: "Start date" 37.1pt, "Alguskuupäev" 54.6pt, of 58 usable.
  startDate: 66,
  dueDate: 73,
  // Wide enough for the word "Comments" at 8pt bold, not just the digits:
  // a header ellipsised to "Comme" names nothing.
  commentCount: 62,
};

/**
 * The thumbnail column: a 104pt-wide picture (the column less its padding)
 * shows what a site photo depicts. Wide enough that the optional text columns
 * give up width to it when many are ticked — see `tableColumns`.
 */
const ATTACHMENT_COLUMN_WIDTH = 112;
/** The title never goes narrower than this. */
const TITLE_MIN_WIDTH = 60;

/** The header cell for a column, in the document's language. */
function columnLabel(key: TextColumnKey, labels: ProtocolDocumentModel["labels"]): string {
  switch (key) {
    case "number":
      return "#";
    case "title":
      return labels.issue;
    case "status":
      return labels.status;
    case "assignees":
      return labels.assignees;
    case "disciplines":
      return labels.disciplines;
    case "startDate":
      return labels.startDate;
    case "dueDate":
      return labels.dueDate;
    case "commentCount":
      return labels.commentCount;
  }
}

/**
 * The table's columns and their widths, for the chosen set.
 *
 * Exported for the invariant test alone: the widths must sum to CONTENT_WIDTH
 * for EVERY subset, and asserting that through a rendered PDF would mean
 * reading text positions back out of a content stream.
 */
export function tableColumns(model: ProtocolDocumentModel): TableColumn[] {
  const optional = PROTOCOL_COLUMNS.filter((key) => model.columns[key]);
  const attachmentWidth = model.attachmentsInTable ? ATTACHMENT_COLUMN_WIDTH : 0;
  const optionalTotal = optional.reduce((sum, key) => sum + NARROW_COLUMN_WIDTHS[key], 0);
  // The thumbnail column is the one column the narrow widths were not sized
  // around (see `startDate`). When it would push the title under its floor,
  // the optional text columns give up the difference in proportion — their
  // text wraps, where a title squeezed below its floor would not fit a word.
  const room = CONTENT_WIDTH - TITLE_MIN_WIDTH - NARROW_COLUMN_WIDTHS.number - attachmentWidth;
  const scale = optionalTotal > room ? room / optionalTotal : 1;
  const widths = optional.map((key) => NARROW_COLUMN_WIDTHS[key] * scale);
  const narrowTotal =
    NARROW_COLUMN_WIDTHS.number + widths.reduce((sum, width) => sum + width, 0) + attachmentWidth;

  return [
    { key: "number", label: "#", width: NARROW_COLUMN_WIDTHS.number },
    // Whatever is left, so the columns always sum to the band drawn behind
    // them. Clamped in case a future column's minimum eats the page — better a
    // squeezed title than one that runs off the right edge.
    {
      key: "title",
      label: model.labels.issue,
      width: Math.max(TITLE_MIN_WIDTH, CONTENT_WIDTH - narrowTotal),
    },
    ...optional.map((key, index) => ({
      key,
      label: columnLabel(key, model.labels),
      width: widths[index],
    })),
    ...(model.attachmentsInTable
      ? [{ key: "attachments" as const, label: model.labels.attachments, width: attachmentWidth }]
      : []),
  ];
}

const HEADER_SIZE = 8;
const ICON_SIZE = 9;

function isIconColumn(key: TableColumn["key"]): key is ProtocolColumnId {
  return (PROTOCOL_COLUMNS as readonly string[]).includes(key);
}

/**
 * Whether the optional columns are headed by icons rather than words: when any
 * of their labels would not fit its column. All of them switch together — a
 * header half words and half icons reads as a rendering fault.
 */
function headerUsesIcons(sheet: PdfSheet, columns: TableColumn[]): boolean {
  return columns.some(
    (column) =>
      isIconColumn(column.key) &&
      sheet.bold.widthOfTextAtSize(toWinAnsi(column.label), HEADER_SIZE) > column.width - 8
  );
}

function drawTableHeader(sheet: PdfSheet, columns: TableColumn[], icons: boolean): void {
  const { headerHeight: height, headerFill, headerText } = sheet.theme.table;
  sheet.page.drawRectangle({
    x: MARGIN,
    y: sheet.y - height,
    width: CONTENT_WIDTH,
    height,
    color: headerFill,
  });
  let x = MARGIN;
  for (const column of columns) {
    if (icons && isIconColumn(column.key)) {
      drawColumnIcon(
        sheet.page,
        column.key,
        x + 4,
        sheet.y - (height - ICON_SIZE) / 2,
        ICON_SIZE,
        headerText
      );
    } else {
      // Clipped, not drawn raw: drawText neither wraps nor clips, and a long
      // header over a narrow column would paint straight over its neighbour.
      drawText(
        sheet,
        ellipsise(column.label, sheet.bold, HEADER_SIZE, column.width - 8),
        x + 4,
        sheet.y - (height - 8),
        {
          font: sheet.bold,
          size: HEADER_SIZE,
          color: headerText,
        }
      );
    }
    x += column.width;
  }
  sheet.y -= height;
}

const KEY_SIZE = 7.5;
const KEY_LINE_HEIGHT = 12;

/** Under an icon-headed table, each icon beside the word it stands for. */
function drawIconKey(sheet: PdfSheet, columns: TableColumn[]): void {
  sheet.y -= 6;
  let x = MARGIN;
  let lineStarted = false;
  for (const column of columns) {
    if (!isIconColumn(column.key)) continue;
    const label = toWinAnsi(column.label);
    const width = ICON_SIZE + 3 + sheet.regular.widthOfTextAtSize(label, KEY_SIZE);
    if (lineStarted && x + width > MARGIN + CONTENT_WIDTH) {
      sheet.y -= KEY_LINE_HEIGHT;
      x = MARGIN;
      lineStarted = false;
    }
    if (!lineStarted) ensureSpace(sheet, KEY_LINE_HEIGHT);
    drawColumnIcon(sheet.page, column.key, x, sheet.y, ICON_SIZE, MUTED);
    drawText(sheet, column.label, x + ICON_SIZE + 3, sheet.y - 7, {
      size: KEY_SIZE,
      color: MUTED,
    });
    x += width + 12;
    lineStarted = true;
  }
  sheet.y -= KEY_LINE_HEIGHT;
}

/** A row must fit one page under its repeated header and its own padding. */
function maxRowLines(sheet: PdfSheet): number {
  const { lineHeight, headerHeight, padding } = sheet.theme.table;
  return linesPerPage(lineHeight, headerHeight + padding);
}

/**
 * A row's wrapped text and the height it needs, measured once.
 *
 * Wrapping a row is not cheap — `wrapText` measures the font per word, and per
 * character for a word too long to fit a column — and the row has to be
 * measured before the page break is decided and then drawn. Measuring in
 * `drawIssues` and handing the result to `drawIssueRow` pays for it once.
 */
type MeasuredRow = {
  lines: Map<TableColumn["key"], string[]>;
  /** The thumbnail cell, when the table has one. */
  thumbs: ReturnType<typeof measureThumbCell> | null;
  height: number;
};

function measureIssueRow(
  sheet: PdfSheet,
  issue: ProtocolDocumentIssue,
  columns: TableColumn[],
  resolved: ResolvedAttachments
): MeasuredRow {
  const { textSize, lineHeight, padding } = sheet.theme.table;
  const maxLines = maxRowLines(sheet);
  const lines = new Map<TableColumn["key"], string[]>();
  let thumbs: MeasuredRow["thumbs"] = null;
  for (const column of columns) {
    const width = column.width - 8;
    if (column.key === "attachments") {
      thumbs = measureThumbCell(sheet, issue.attachments, resolved, width);
      continue;
    }
    lines.set(
      column.key,
      capLines(
        wrapText(issue[column.key], sheet.regular, textSize, width),
        sheet.regular,
        textSize,
        width,
        maxLines
      )
    );
  }
  const lineCount = Math.max(...Array.from(lines.values()).map((value) => value.length), 1);
  // A row with pictures grows to hold them; the text keeps its own top.
  const height = Math.max(lineCount * lineHeight + padding, (thumbs?.height ?? 0) + padding);
  return { lines, thumbs, height };
}

/** The row's own frame, in the layout's treatment. `index` is the row's place
 *  in the table, which the striped treatment alternates on. */
function drawRowFrame(sheet: PdfSheet, height: number, index: number): void {
  const bottom = sheet.y - height;
  switch (sheet.theme.table.rows) {
    case "boxed":
      sheet.page.drawRectangle({
        x: MARGIN,
        y: bottom,
        width: CONTENT_WIDTH,
        height,
        borderColor: RULE,
        borderWidth: 0.45,
      });
      return;
    case "striped":
      if (index % 2 === 1) {
        sheet.page.drawRectangle({
          x: MARGIN,
          y: bottom,
          width: CONTENT_WIDTH,
          height,
          color: SURFACE,
        });
      }
      return;
    case "ruled":
      sheet.page.drawLine({
        start: { x: MARGIN, y: bottom },
        end: { x: MARGIN + CONTENT_WIDTH, y: bottom },
        thickness: 0.4,
        color: RULE,
      });
      return;
  }
}

function drawIssueRow(
  sheet: PdfSheet,
  row: MeasuredRow,
  columns: TableColumn[],
  index: number
): void {
  const { lines, height } = row;
  const { lineHeight, textSize, baselineOffset, padding } = sheet.theme.table;
  drawRowFrame(sheet, height, index);
  let x = MARGIN;
  for (const column of columns) {
    if (column.key === "attachments" && row.thumbs) {
      drawThumbCell(sheet, row.thumbs, x + 4, sheet.y - padding / 2, column.width - 8);
    }
    (lines.get(column.key) ?? []).forEach((line, lineIndex) => {
      drawText(sheet, line, x + 4, sheet.y - baselineOffset - lineIndex * lineHeight, {
        size: textSize,
      });
    });
    x += column.width;
  }
  sheet.y -= height;
}

async function drawIssues(
  sheet: PdfSheet,
  model: ProtocolDocumentModel,
  resolved: ResolvedAttachments,
  pace: Pacer
): Promise<void> {
  // Guarded on the flag, never on `model.issues` being empty: an empty array
  // already means "the period held no issues", and a table the author excluded
  // must not read the same as a period that had nothing in it.
  if (!model.include.issues) return;
  const columns = tableColumns(model);
  const icons = headerUsesIcons(sheet, columns);
  if (model.issues.length === 0) {
    drawTableHeader(sheet, columns, icons);
    sheet.y -= 12;
    sheet.y -= drawWrapped(sheet, model.labels.noActivity, MARGIN, sheet.y, CONTENT_WIDTH, {
      size: 10,
      color: MUTED,
    });
    if (icons) drawIconKey(sheet, columns);
    return;
  }
  // The header is drawn only once the row under it is known to fit: reserving
  // the row first means the first row can never push a bare header band to the
  // foot of the previous page and repeat it on the next.
  let headerDrawn = false;
  // Counted from each header, so a striped table restarts its stripes under the
  // header a page break repeats rather than opening a page on a shaded row.
  let rowUnderHeader = 0;
  for (const issue of model.issues) {
    const row = measureIssueRow(sheet, issue, columns, resolved);
    const brokePage = ensureSpace(sheet, row.height + sheet.theme.table.headerHeight);
    if (brokePage || !headerDrawn) {
      drawTableHeader(sheet, columns, icons);
      headerDrawn = true;
      rowUnderHeader = 0;
    }
    // Where an attachment's way back lands: the row, with a little of the
    // header's edge above it.
    sheet.links.issueRows.set(issue.id, { page: sheet.page, top: sheet.y + 6 });
    drawIssueRow(sheet, row, columns, rowUnderHeader);
    rowUnderHeader += 1;
    await pace();
  }
  if (icons) drawIconKey(sheet, columns);
}

function drawFooters(sheet: PdfSheet, model: ProtocolDocumentModel): void {
  const pages = sheet.doc.getPages();
  pages.forEach((page, index) => {
    // An attached PDF's pages count towards the total — they are pages of this
    // file — but their foot is somebody else's drawing.
    if (sheet.foreignPages.has(page)) return;
    const label = fill(model.labels.page, {
      current: String(index + 1),
      total: String(pages.length),
    });
    const width = sheet.regular.widthOfTextAtSize(toWinAnsi(label), 8);
    page.drawText(toWinAnsi(label), {
      x: PAGE_WIDTH - MARGIN - width,
      y: 24,
      size: 8,
      font: sheet.regular,
      color: MUTED,
    });
  });
}

/** What a render learnt that its bytes do not say on their own. */
export interface ProtocolRenderStats {
  /** Attached files the PDF lists as "could not be included". */
  attachmentsNotIncluded: number;
}

/**
 * The document, drawn. `signal` cuts the render short between rows rather than
 * only suppressing its result — see `pacer`. It rejects with an `AbortError`,
 * which every caller already treats as "cancelled", not as a failure.
 *
 * `stats`, when given, is filled in as the render goes — so the caller can say
 * out loud what the document only says beside each file.
 */
export async function renderProtocolPdf(
  model: ProtocolDocumentModel,
  signal?: AbortSignal,
  stats?: ProtocolRenderStats
): Promise<Uint8Array> {
  const pace = pacer(signal);
  throwIfAborted(signal);
  const doc = await PDFDocument.create();
  const sheet: PdfSheet = {
    doc,
    page: doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]),
    y: PAGE_HEIGHT - MARGIN,
    regular: await doc.embedFont(StandardFonts.Helvetica),
    bold: await doc.embedFont(StandardFonts.HelveticaBold),
    logo: await embedBrandLogo(doc),
    theme: protocolPdfTheme(model.layout),
    foreignPages: new Set(),
    links: createProtocolLinks(),
  };
  doc.setTitle(toWinAnsi(model.title), { showInWindowTitleBar: true });
  doc.setAuthor("Franklin");
  doc.setCreator("Franklin");
  doc.setLanguage(model.language === "et" ? "et-EE" : "en-GB");

  // The setup above awaits a font embed and a logo fetch, which is long enough
  // for a cancel to be dispatched — and a document with no rows at all never
  // reaches `pace`.
  throwIfAborted(signal);
  // Read once, before anything is drawn: the table and the appendix draw the
  // same embedded pictures, and neither may promise one that will not come.
  const resolved = await resolveAttachments(sheet, model, pace);
  if (stats) stats.attachmentsNotIncluded = countNotIncluded(resolved);
  drawHeader(sheet, model);
  drawMetadata(sheet, model);
  drawSummary(sheet, model);
  await drawNotes(sheet, model, pace);
  await drawIssues(sheet, model, resolved, pace);
  await drawComments(sheet, model, pace);
  await drawActivity(sheet, model, pace);
  await drawAttachments(sheet, model, resolved, pace);
  drawFooters(sheet, model);
  await finishProtocolLinks(doc, sheet.links, originalsOf(model));
  return doc.save();
}

/** The files whose own bytes ride along, by number. */
function originalsOf(
  model: ProtocolDocumentModel
): Map<number, { filename: string; original: ProtocolOriginal }> {
  const originals = new Map<number, { filename: string; original: ProtocolOriginal }>();
  for (const file of model.attachments?.files ?? []) {
    if (file.original)
      originals.set(file.number, { filename: file.filename, original: file.original });
  }
  return originals;
}

/**
 * Renders the protocol and hands it to the browser as a download.
 *
 * Returns the bytes so a caller that also wants to FILE the document (the
 * "Save to project" card) works from the very object the user downloaded,
 * rather than rendering the same document a second time and hoping the two
 * agree. `null` means the export was cancelled and nothing was written.
 */
export async function downloadProtocolPdf(
  model: ProtocolDocumentModel,
  filename: string,
  signal?: AbortSignal,
  stats?: ProtocolRenderStats
): Promise<Uint8Array | null> {
  const bytes = await renderProtocolPdf(model, signal, stats);
  // A cancel that lands too late for the render to stop — during doc.save(),
  // after the last row was drawn — is still after the caller's own check. A
  // cancelled export must not drop a file on the user.
  if (signal?.aborted) return null;
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  // Firefox ignores a programmatic click on a detached anchor, so the anchor
  // has to be in the document — same as every other download path here.
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    // A throwing click (an extension patching HTMLAnchorElement, a page-level
    // handler) must not leave the anchor in the body and the blob — a whole
    // PDF — pinned for the life of the tab.
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
  return bytes;
}
